import { withDependencies, named } from '@wix/thunderbolt-ioc'
import { IViewerManagerImplementor, ScrollToCallbacks, Callback } from 'thunderbolt-viewer-manager-types'
import { ISiteScrollBlocker, SiteScrollBlockerSymbol } from 'feature-site-scroll-blocker'
import { IWindowScrollAPI, WindowScrollApiSymbol } from 'feature-window-scroll'
import { EditorFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import type { ViewerManagerScrollApiEditorConfig, IRootScrollApi } from './types'
import { NavigationManagerSymbol, INavigationManager } from 'feature-navigation-manager'
import { IPageProvider, PageProviderSymbol } from 'feature-pages'
import _ from 'lodash'
import { InnerScrollApiSymbol, InnerScrollApiType } from '@wix/thunderbolt-becky-types'
import { name, RootScrollApiSymbol } from './symbols'

const viewerManagerScrollApiFactory = (
	siteScrollBlocker: ISiteScrollBlocker,
	config: ViewerManagerScrollApiEditorConfig,
	navigationManager: INavigationManager,
	pageProvider: IPageProvider,
	innerScrollApi: InnerScrollApiType,
	rootScrollApi: IRootScrollApi
): IViewerManagerImplementor<'scroll'> => {
	const scrollToComponent = async (compId: string, callbacks: Partial<ScrollToCallbacks>): Promise<void> => {
		const reflector = await pageProvider('masterPage', 'masterPage')

		if (navigationManager.isDuringNavigation()) {
			await navigationManager.waitForNavigationEnd()
		}

		const [windowScroll] = reflector.getAllImplementersOf<IWindowScrollAPI>(WindowScrollApiSymbol)
		windowScroll.scrollToComponent(compId, { callbacks })
	}

	const scroll = (compId: string, x: number, y: number, isRelative: boolean, callback: Callback) => {
		if (!innerScrollApi.isScrollable(compId)) {
			return
		}

		if (isRelative) {
			innerScrollApi.scrollBy(compId, x, y)
		} else {
			innerScrollApi.scrollTo(compId, x, y)
		}

		if (_.isFunction(callback)) {
			callback()
		}
	}

	return {
		scroll: {
			isSiteScrollingBlocked: (): boolean => siteScrollBlocker.isScrollingBlocked(),
			scrollToComponent,
			scrollToAnchor: async (dataQuery: string, progressCallback: Callback): Promise<void> => {
				// In EditorX first argument is anchorData ({anchorDataId: string, compId: string})
				// In classic Editor first argument is anchor dataQuery
				const compId = config.dataIdToCompId[dataQuery.replace('#', '')]
				scrollToComponent(compId, {
					onUpdate: progressCallback,
				})
			},
			// TODO :TB-3923 - Implement for EditorX
			getTotalScroll: (compId: string): { x: number; y: number } => {
				const compParent = config.compIdToParentId[compId]
				return (compParent && innerScrollApi.getComponentInnerScroll(compParent)) || { x: 0, y: 0 }
			},
			scroll: (compId: string, x: number, y: number, callback: Callback): void =>
				scroll(compId, x, y, false, callback),
			scrollBy: (compId: string, x: number, y: number, callback: Callback): void =>
				scroll(compId, x, y, true, callback),
			setOnComponentsScrollListener: innerScrollApi.setOnComponentsScrollListener,
			setScroll: rootScrollApi.setScroll,
			getScroll: rootScrollApi.getScroll,
			setScrollAndScale: rootScrollApi.setScrollAndScale,
			animateScroll: rootScrollApi.animateScroll,
		},
	}
}

export const ViewerManagerScrollApi = withDependencies(
	[
		SiteScrollBlockerSymbol,
		named(EditorFeatureConfigSymbol, name),
		NavigationManagerSymbol,
		PageProviderSymbol,
		InnerScrollApiSymbol,
		RootScrollApiSymbol,
	],
	viewerManagerScrollApiFactory
)
